/**
 * Motion helpers. The look of every movement lives in styles/motion.css;
 * these only keep a closing thing on screen long enough to leave, and tell
 * a component when the reader has asked for no motion at all.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

/** How long a dialog, a menu or a toast takes to leave. Matches --t-exit. */
export const EXIT_MS = 160

const REDUCED = '(prefers-reduced-motion: reduce)'

/** The reader asked the system for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.(REDUCED).matches)
}

/**
 * Keeps a closed thing mounted for its exit. `mounted` says whether to draw
 * it, `closing` that it is on its way out: draw it with `is-closing` and
 * leave it inert. With reduced motion it goes at once.
 *
 * The switch to closing happens in the same render as the close, so the
 * thing is never unmounted for a frame and mounted again.
 */
export function usePresence(open: boolean, exitMs = EXIT_MS): { mounted: boolean; closing: boolean } {
  const [seen, setSeen] = useState({ open, lingering: false })
  if (seen.open !== open) {
    setSeen({ open, lingering: !open && !prefersReducedMotion() })
  }
  const lingering = seen.open === open ? seen.lingering : !open && !prefersReducedMotion()

  useEffect(() => {
    if (!lingering) return
    const t = window.setTimeout(() => setSeen((s) => ({ ...s, lingering: false })), exitMs)
    return () => window.clearTimeout(t)
  }, [lingering, exitMs])

  return { mounted: open || lingering, closing: !open && lingering }
}

/** The browser can crossfade one page into the next (View Transitions). */
function crossfades(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document
}

/**
 * Every navigation the app makes crossfades: the page fades out as the next
 * fades in, and the chrome around it, the same on both, holds still. Wraps
 * the router once where it is made. A browser without View Transitions, and
 * a reader who asked for less motion, get the plain change.
 */
export function withPageTransitions<R extends { navigate: (...args: never[]) => unknown }>(router: R): R {
  const navigate = router.navigate.bind(router) as (to: unknown, opts?: { viewTransition?: boolean }) => unknown
  const wrapped = (to: unknown, opts?: { viewTransition?: boolean }) =>
    typeof to === 'number'
      ? navigate(to, opts)
      : navigate(to, { ...opts, viewTransition: opts?.viewTransition ?? !prefersReducedMotion() })
  router.navigate = wrapped as unknown as R['navigate']
  return router
}

/**
 * Fades the page in whenever the path changes, in a browser that cannot
 * crossfade (the router replays its crossfades on back and forward too). A
 * filter or a tab on the same path keeps the page still. Mounted once at
 * the root of each app; it draws nothing.
 *
 * Only opacity moves on <main>, so nothing fixed inside it loses its place;
 * the cards inside rise on their own (motion.css).
 */
export function PageEnter(): null {
  const { pathname } = useLocation()
  const first = useRef(true)
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    if (prefersReducedMotion() || crossfades()) return
    document
      .getElementById('main')
      ?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' })
  }, [pathname])
  return null
}

/**
 * Marks a figure that has just changed, so the eye catches the move: up and
 * down for a number, a plain mark for any other change. Nothing on the first
 * draw and nothing while the value stays. `key` changes with every move: put
 * it on the element so the same mark twice in a row plays twice.
 */
export function useFlash(value: number | string | null | undefined): { className?: string; key: number } {
  const prev = useRef(value)
  const [mark, setMark] = useState<{ className?: string; key: number }>({ key: 0 })
  useEffect(() => {
    const before = prev.current
    prev.current = value
    if (before === value || before === null || before === undefined || value === null || value === undefined) return
    const className =
      typeof value === 'number' && typeof before === 'number'
        ? value > before
          ? 'is-flash-up'
          : 'is-flash-down'
        : 'is-flash'
    setMark((m) => ({ className, key: m.key + 1 }))
  }, [value])
  return mark
}
